import { Dispatch } from 'redux';
import { ProjectMemberJSON } from '@projectMembers/models/projectMember';
import {
	BUY_ADD_ON,
	DELETE_PROJECT_MEMBER,
	FETCH_PROJECT_MEMBERS,
	INVITE_WORKSPACE_MEMBER,
	MARK_ASSET_COMMENTS_AS_COMPLETED,
	REMOVE_ADD_ON,
	UPDATE_PROJECT_MEMBER_ROLE,
	UPDATE_PROJECT_PROPERTIES,
	UPDATE_WORKSPACE_PROPERTIES
} from 'src/actions/types';
import { ProjectMemberRoleType } from '@projectMembers/models/projectRole';
import { UserJson } from '../models/user/user';
import { AssetJSON } from '../modules/projectView/models/asset';
import { FolderJSON, OrganizerType } from '../modules/projectView/models/folder';

export const UPDATER_ACTION_TYPE = {
	currentUser: '__UPDATE_CURRENT_USER',
	assets: '__UPDATE_ASSETS',
	folders: '__UPDATE_FOLDERS',
	asset: '__UPDATE_ASSET',
	assetsWith: '__UPDATE_ASSETS_WITH',
	foldersWith: '__UPDATE_FOLDERS_WITH',
	workspaceOnboardingMarkedAsClosed: '__MARK_WORKSPACE_ONBOARDING_AS_CLOSED',
	createOrUpdateAsset: '__CREATE_OR_UPDATE_ASSET'
} as const

/*
* This class is meant to update the redux store when a change with react query is made.
*/

export default class ReduxStoreUpdater {
	dispatch: Dispatch;

	constructor( dispatch: Dispatch ) {
		this.dispatch = dispatch;
	}

	updateCurrentUser( user: UserJson ) {
		this.dispatch( { type: UPDATER_ACTION_TYPE.currentUser, payload: { user } } );
	}

	replaceProjectAssets(
		assets: AssetJSON[], { projectID, replaceAll = false }: { projectID: number, replaceAll?: boolean }
	) {
		this.dispatch( {
			type: UPDATER_ACTION_TYPE.assets,
			payload: assets,
			meta: { projectID, replaceAll }
		} );
	}

	updateAssets( assetIDs: number[], updater: ( asset: AssetJSON ) => AssetJSON ) {
		this.dispatch( {
			type: UPDATER_ACTION_TYPE.assetsWith,
			payload: updater,
			meta: { assetIDs }
		} );
	}

	updateAsset( assetID: number, updater: ( asset: AssetJSON ) => AssetJSON ) {
		this.dispatch( {
			type: UPDATER_ACTION_TYPE.asset,
			payload: updater,
			meta: { assetID }
		} );
	}

	createOrUpdateAsset( asset: AssetJSON, projectID: number ) {
		this.dispatch( {
			type: UPDATER_ACTION_TYPE.createOrUpdateAsset,
			payload: asset,
			meta: { assetID: asset.id, projectID }
		} );
	}

	replaceOrganizerFolders(
		folders: FolderJSON[],
		{ organizerType, organizerID }: { organizerType: OrganizerType, organizerID: number }
	) {
		this.dispatch( {
			type: UPDATER_ACTION_TYPE.folders,
			payload: folders,
			meta: { organizerType, organizerID }
		} );
	}

	updateFolders( folderIDs: number[], updater: ( folder: FolderJSON ) => FolderJSON ) {
		this.dispatch( {
			type: UPDATER_ACTION_TYPE.foldersWith,
			payload: updater,
			meta: { folderIDs }
		} );
	}

	markWorkspaceOnboardingAsClosed( workspaceID: number ) {
		this.dispatch( {
			type: UPDATER_ACTION_TYPE.workspaceOnboardingMarkedAsClosed,
			meta: { workspaceID }
		} );
	}

	replaceProjectMembers( projectID: number, members: ProjectMemberJSON[] ) {
		this.dispatch( {
			type: FETCH_PROJECT_MEMBERS,
			payload: members,
			meta: {
				projectID,
				'redux-pack/LIFECYCLE': 'success',
				'redux-pack/TRANSACTION': 'redux-store-updater'
			}
		} );
	}

	updateProjectMemberRole( projectID: number, memberID: number, role: ProjectMemberRoleType ) {
		this.dispatch( {
			type: UPDATE_PROJECT_MEMBER_ROLE,
			meta: { projectID, memberID, role }
		} );
	}

	deleteProjectMember( projectID: number, memberID: number ) {
		this.dispatch( {
			type: DELETE_PROJECT_MEMBER,
			meta: {
				projectID,
				memberID,
				'redux-pack/LIFECYCLE': 'success',
				'redux-pack/TRANSACTION': 'redux-store-updater'
			}
		} );
	}

	updateCurrentUserRoleInProject( projectID: number, role: ProjectMemberRoleType ) {
		this.dispatch( {
			type: UPDATE_PROJECT_PROPERTIES,
			payload: { current_user_role: { type: role } },
			meta: { projectID }
		} );
	}

	updateProjectProperties( projectID: number, properties: Record<string, unknown> ) {
		this.dispatch( {
			type: UPDATE_PROJECT_PROPERTIES,
			payload: properties,
			meta: { projectID }
		} );
	}

	updateWorkspaceProperties( workspaceID: number, properties: Record<string, unknown> ) {
		this.dispatch( {
			type: UPDATE_WORKSPACE_PROPERTIES,
			payload: properties,
			meta: { workspaceID }
		} );
	}

	markAssetCommentsAsResolved( assetID: number, currentUserID?: number ) {
		this.dispatch( {
			type: MARK_ASSET_COMMENTS_AS_COMPLETED,
			meta: { assetID, currentUserID }
		} );
	}

	workspaceAddOnBought( workspace: unknown ) {
		this.dispatchWorkspaceSuccess( BUY_ADD_ON, workspace );
	}

	workspaceAddOnRemoved( workspace: unknown ) {
		this.dispatchWorkspaceSuccess( REMOVE_ADD_ON, workspace );
	}

	workspaceMembersInvited( workspace: unknown ) {
		this.dispatchWorkspaceSuccess( INVITE_WORKSPACE_MEMBER, workspace );
	}

	private dispatchWorkspaceSuccess( type: string, workspace: unknown ) {
		this.dispatch( {
			type,
			payload: workspace,
			meta: {
				'redux-pack/LIFECYCLE': 'success',
				'redux-pack/TRANSACTION': 'redux-store-updater'
			}
		} );
	}
}
